import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { InlineNotification } from '@carbon/react';
import { useAtom } from 'jotai';
import type { ColumnFiltersState } from '@tanstack/react-table';

import {
  ColumnCheckboxGroupFilter,
  ColumnDateFilter,
  DataTable,
  FilterSection,
  type DataTableColumnDef,
} from 'components/data-table';
import { Link } from 'components/link/link';
import type {
  DataTableSelectionState,
  InitialTableState,
  TableState,
} from 'components/data-table/types';

import {
  entitySourceSelectAllAtom,
  type PackageEntitySearchAtomPair,
} from './atoms';
import { useSourceTableSelection } from './hooks';
import { handleSourceNameClick, stopRowClick } from './source-selection';
import type { EntityRow, SourceSelectionChange } from './types';
import { entityFilterStateToParams } from './utils';

const DEFAULT_PAGE_SIZE = 25;

const ENTITY_CLASSIFICATION_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'aliases', label: 'Aliases' },
];

const ENTITY_TYPE_OPTIONS = [
  { value: 'Individual', label: 'Individual' },
  { value: 'Organization', label: 'Organization' },
  { value: 'Imported', label: 'Imported' },
];

/** Multi-select wiring for the Step 0 source picker. */
export interface EntityMultiSelect {
  /** Every selected entity id, across pages. The form owns this. */
  selectedIds: Set<number>;
  /** Fired with the resolved delta for both row clicks and select-all. */
  onSelectionChange: (change: SourceSelectionChange) => void;
  /** Whether the entity tile is the one picked. */
  active: boolean;
}

/**
 * Selection mode — exactly one of the two, enforced by the compiler so a
 * caller can't omit both and get a silently inert table.
 */
type EntityTableSelection =
  | {
      /**
       * Multi-select mode (Step 0). Adds the checkbox column and a header
       * select-all that covers the whole filtered match set, not just the page.
       */
      multiSelect: EntityMultiSelect;
      selectedId?: never;
      onSelect?: never;
    }
  | {
      multiSelect?: never;
      /**
       * Single-select mode: currently-highlighted entity id, `null` when
       * nothing is selected.
       */
      selectedId: number | null;
      /** Single-select mode: fired on every row click with the full row. */
      onSelect: (row: EntityRow) => void;
    };

/** Props for the {@link PackageEntityTable} component */
export type PackageEntityTableProps = {
  /**
   * Feature-local entity `searchAtomPair` to back this table. Each consumer
   * (Step 0 source picker, Step 2 acquiring-entity drawer) injects its own
   * instance so paging / search state stays isolated per step.
   */
  searchAtom: PackageEntitySearchAtomPair;
  /**
   * Optional CSV export handler. When provided, DataTable renders an
   * "Export to CSV" toolbar action. Step 0's source picker wires this to
   * `/queries/entities/export`; Step 2's acquirer drawer omits it.
   */
  onExport?: () => void | Promise<void>;
} & EntityTableSelection;

/**
 * Column set. The two link cells differ by selection mode: in multi-select a
 * plain click on the name toggles the row instead of navigating, and the id
 * link (which points at the same entity but is not the row's primary label)
 * keeps its navigation without toggling.
 *
 * Built once per mode at module scope so the identity stays stable across
 * renders (DataTable memoizes on it).
 *
 * @param {boolean} isMulti - Whether the table is in multi-select mode
 * @return {DataTableColumnDef[]} Column definitions
 */
const buildColumns = (isMulti: boolean): DataTableColumnDef<EntityRow>[] => [
  {
    id: 'id',
    accessorKey: 'id',
    header: 'Entity ID',
    meta: { type: 'integer' as const },
    cell: ({ row }) => (
      <Link
        to={row.original.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={isMulti ? stopRowClick : undefined}
      >
        {row.original.id}
      </Link>
    ),
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Entity Name',
    cell: ({ row }) => (
      <Link
        to={row.original.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={isMulti ? handleSourceNameClick : undefined}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'entityClassification',
    accessorKey: 'entityClassification',
    header: 'Entity Classification',
    meta: {
      type: 'enum' as const,
      filterOptions: ENTITY_CLASSIFICATION_OPTIONS,
    },
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created Date',
    meta: { type: 'date' as const },
  },
  {
    id: 'entityType',
    accessorKey: 'entityType',
    header: 'Entity Type',
    meta: { type: 'enum' as const, filterOptions: ENTITY_TYPE_OPTIONS },
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Updated Date',
    meta: { type: 'date' as const },
  },
  {
    id: 'aliasCount',
    accessorKey: 'aliasCount',
    header: '# of Aliases',
    meta: { type: 'integer' as const },
  },
  {
    id: 'documentCount',
    accessorKey: 'documentCount',
    header: '# of Documents',
    meta: { type: 'integer' as const },
  },
  {
    id: 'capacityCount',
    accessorKey: 'capacityCount',
    header: '# of Capacities',
    meta: { type: 'integer' as const },
  },
  {
    id: 'crmAccountCount',
    accessorKey: 'crmAccountCount',
    header: '# of CRM Accounts',
    meta: { type: 'integer' as const },
  },
  {
    id: 'crmTractCount',
    accessorKey: 'crmTractCount',
    header: '# of CRM Tracts',
    meta: { type: 'integer' as const },
  },
  {
    id: 'titleAssetCount',
    accessorKey: 'titleAssetCount',
    header: '# of Title Assets',
    meta: { type: 'integer' as const },
  },
];

const SINGLE_SELECT_COLUMNS = buildColumns(false);
const MULTI_SELECT_COLUMNS = buildColumns(true);

const NO_SELECTION_CHANGE = (): void => undefined;
const NO_SELECTED_IDS = new Set<number>();

const getRowId = (row: EntityRow): string => String(row.id);

/**
 * Entity table backed by a caller-injected `searchAtomPair`, in one of two
 * selection modes:
 *
 *  - single (Step 2's acquiring-entity drawer) — row click fires
 *    `onSelect(row)`; commit semantics live in the caller.
 *  - multi (Step 0's source picker) — checkbox column, row click and name
 *    link both toggle, and the header select-all resolves the whole filtered
 *    match set through `/queries/entities/select_all` (the ids the user just
 *    selected include rows off the current page).
 *
 * Distinct from `components/entity-search-table` (the CRM-account-aware
 * primitive used by the CRM tract wizard) — this one is a thin wrapper
 * over `DataTable` for the package wizard's flows.
 *
 * @param {PackageEntityTableProps} props - Injected atom, selection mode, export
 * @return {JSX.Element} The entity table
 */
export const PackageEntityTable: FC<PackageEntityTableProps> = ({
  searchAtom,
  selectedId = null,
  onSelect,
  multiSelect,
  onExport,
}) => {
  const [{ data, loading, currentPayload }, setSearchPayload] = useAtom(
    searchAtom.searchAtom
  );
  const isMulti = multiSelect !== undefined;

  // `searchAtomPair`'s writer fires a request on every call regardless of
  // payload identity, so the previous `(current) => current ?? defaults`
  // shape would refetch on every remount. Gate on `currentPayload` to seed
  // exactly once per atom lifetime.
  useEffect(() => {
    if (currentPayload === undefined) {
      setSearchPayload(() => ({ page: 1, pageSize: DEFAULT_PAGE_SIZE }));
    }
  }, [setSearchPayload, currentPayload]);

  // `page` / `pageSize` / `globalFilter` are consumed once by DataTable via
  // lazy-init `useState`, so snapshot them on first mount and never refresh.
  // `selection` is reactive — DataTable deep-compares and syncs.
  const [initialPagination] = useState(() => ({
    page: currentPayload?.page,
    pageSize: currentPayload?.pageSize,
    globalFilter: currentPayload?.text,
  }));
  const initialTableState = useMemo<InitialTableState>(
    () => ({ ...initialPagination }),
    [initialPagination]
  );

  // The atom is typed against the shared `EntityBaseResponse` to satisfy
  // `searchAtomPair`'s `ApiQueryResponse` constraint. The `/queries/entities`
  // jbuilder partial also emits classification + count fields; narrow to
  // `EntityRow` here so the column accessors stay typed.
  const rows = useMemo(
    () => (data?.results ?? []) as unknown as EntityRow[],
    [data?.results]
  );

  // Controlled selection: the caller's selection is the source of truth; the
  // table reflects it and reports clicks via `onSelectionChange`. The hook runs
  // in single-select mode too (hooks can't be conditional); its answer is
  // ignored there.
  const totalCount = data?.totalCount ?? 0;
  const {
    controlledSelection: multiSelection,
    handleSelectionChange: handleMultiSelectionChange,
    isSelectingAll,
    selectAllError,
  } = useSourceTableSelection({
    selectAllAtom: entitySourceSelectAllAtom,
    payload: currentPayload,
    rows,
    totalCount,
    selectedIds: multiSelect?.selectedIds ?? NO_SELECTED_IDS,
    active: multiSelect?.active ?? true,
    onSelectionChange: multiSelect?.onSelectionChange ?? NO_SELECTION_CHANGE,
  });
  const controlledSelection = useMemo<DataTableSelectionState>(() => {
    if (isMulti) return multiSelection;
    return {
      selectAll: false,
      toggledNodes: selectedId === null ? [] : [String(selectedId)],
    };
  }, [isMulti, multiSelection, selectedId]);

  const loadData = useCallback(
    ({ page, pageSize, globalFilter }: TableState) => {
      setSearchPayload((current) => ({
        ...current,
        page,
        pageSize,
        text: globalFilter || undefined,
      }));
    },
    [setSearchPayload]
  );

  // Filter changes invalidate the current page (results may shrink), so
  // reset to page 1 alongside the translated filter patch.
  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      setSearchPayload((current) => ({
        ...current,
        ...entityFilterStateToParams(filters),
        page: 1,
      }));
    },
    [setSearchPayload]
  );

  const handleSelectionChange = useCallback(
    (next: DataTableSelectionState) => {
      if (isMulti) {
        handleMultiSelectionChange(next);
        return;
      }
      const id = next.toggledNodes[0];
      if (id === undefined) return;
      const row = rows.find((candidate) => getRowId(candidate) === id);
      if (row) onSelect?.(row);
    },
    [isMulti, handleMultiSelectionChange, rows, onSelect]
  );

  return (
    <>
      {selectAllError !== null && (
        <InlineNotification
          kind="error"
          title="Couldn't update the selection"
          subtitle={selectAllError}
          lowContrast
        />
      )}
      <DataTable<EntityRow>
        data={rows}
        columns={isMulti ? MULTI_SELECT_COLUMNS : SINGLE_SELECT_COLUMNS}
        getRowId={getRowId}
        isLoading={loading || isSelectingAll}
        // Freeze selection while EITHER fetch is in flight — see the matching
        // comment in steps/source-package-table.tsx for why (a click during a
        // refetch can be misread as a header click, and a misread header click
        // selects every match).
        disableSelection={isSelectingAll || loading}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount,
        }}
        onPaginationChange={loadData}
        onSortChange={loadData}
        onGlobalSearchChange={loadData}
        config={{
          selectionType: isMulti ? 'multi' : 'single',
          rowClickSelection: isMulti,
          globalSearch: true,
          columnFilters: true,
          ...(onExport && {
            export: {
              exportPresets: [{ name: 'Export', format: 'csv', onExport }],
            },
          }),
        }}
        initialTableState={initialTableState}
        controlledState={{ selection: controlledSelection }}
        searchPlaceholder="Search by Entity ID or Name"
        onSelectionChange={(s) => handleSelectionChange(s.selection)}
        onFilterChange={(s) => handleFilterChange(s.columnFilters)}
        renderFilterPanel={() => (
          <>
            <FilterSection title="Date Filters">
              <ColumnDateFilter columnId="createdAt" label="Created Date" />
              <ColumnDateFilter columnId="updatedAt" label="Updated Date" />
            </FilterSection>
            <FilterSection title="Entity Classification">
              <ColumnCheckboxGroupFilter
                columnId="entityClassification"
                options={ENTITY_CLASSIFICATION_OPTIONS}
              />
            </FilterSection>
            <FilterSection title="Entity Type">
              <ColumnCheckboxGroupFilter
                columnId="entityType"
                options={ENTITY_TYPE_OPTIONS}
              />
            </FilterSection>
          </>
        )}
      />
    </>
  );
};
